import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DoDisturbOnOutlinedIcon from '@mui/icons-material/DoDisturbOnOutlined';
import { debounce, filter, isEmpty, isNil, size } from 'lodash';

import { listCatalogItemsForSigApi } from 'api/sig';
import { AssociationStatusFilter, AssociationStatusFilterLabel } from 'enums/SigManagement.enum';

import describeCatalogItem from '../utils/describeCatalogItem';
import AssociationCard from './AssociationCard';
import AssociationStatusChip from './AssociationStatusChip';
import { ACTION_GROUP_SX, ROW_SX, TEXT_SX } from './associationRowStyles';

const MINIMUM_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 400;

const statusFilterOptions = [
  { value: AssociationStatusFilter.ALL, label: AssociationStatusFilterLabel.ALL },
  { value: AssociationStatusFilter.ACTIVE, label: AssociationStatusFilterLabel.ACTIVE },
  { value: AssociationStatusFilter.INACTIVE, label: AssociationStatusFilterLabel.INACTIVE },
];

const applyStatusFilter = (catalogItems, statusFilter) => {
  if (statusFilter === AssociationStatusFilter.ALL) {
    return catalogItems;
  }

  return filter(
    catalogItems,
    ({ isActive }) => isActive === (statusFilter === AssociationStatusFilter.ACTIVE)
  );
};

/**
 * The catalog items this sig is approved for, and the staged changes to them.
 *
 * Two things worth knowing.
 *
 * An approval is held against one catalog variant — a drug at a strength and pack size, not a drug
 * name — so the picker offers variants and every row shows its packaging. Picking by name alone
 * would attach the approval to whichever variant happened to sort first.
 *
 * Setting an item inactive keeps the row and flips its status, exactly as it does for a direction.
 * It is not a delete, so it is reversible and the record of what was once approved survives.
 */
const SigCatalogAssociationsPanel = ({
  catalogItems,
  catalogItemCount,
  newCatalogItems,
  loading,
  isCreating,
  onStageStatus,
  onStageNew,
  onUnstageNew,
}) => {
  const [statusFilter, setStatusFilter] = useState(AssociationStatusFilter.ALL);
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedCatalogItem, setSelectedCatalogItem] = useState(null);
  const visibleCatalogItems = applyStatusFilter(catalogItems, statusFilter);

  // Debounced because it hits the catalog on the other database; a request per keystroke would
  // queue up behind itself, and the last answer to arrive is not necessarily the last one asked.
  const searchCatalog = useMemo(
    () =>
      debounce((drugName) => {
        listCatalogItemsForSigApi({ drugName })
          .then((foundItems) => setOptions(foundItems))
          .catch(() => setOptions([]))
          .finally(() => setSearching(false));
      }, SEARCH_DEBOUNCE_MS),
    []
  );

  const handleSearchChange = (_, value) => {
    if (size(value) < MINIMUM_SEARCH_LENGTH) {
      setOptions([]);
      setSearching(false);

      return;
    }

    setSearching(true);
    searchCatalog(value);
  };

  // Why an option is unpickable, or undefined when it is pickable. The picker has always disabled
  // an item the sig already holds; it never said so, and for an inactive approval the way out is
  // the Set active button in the list rather than a second row that cannot be added.
  const reasonItIsUnpickable = (catalogId) => {
    const listed = catalogItems.find((item) => item.catalogId === catalogId);

    if (listed) {
      return listed.isActive
        ? 'Already on this sig'
        : 'Already on this sig but inactive — set it active in the list';
    }

    return newCatalogItems.some((item) => item.catalogId === catalogId)
      ? 'Already staged to be added'
      : undefined;
  };

  const handleAddCatalogItem = () => {
    onStageNew(selectedCatalogItem);
    setSelectedCatalogItem(null);
    setOptions([]);
  };

  const nothingToShow = !loading && size(visibleCatalogItems) === 0 && size(newCatalogItems) === 0;

  return (
    <AssociationCard
      // The staged list while creating, for the same reason as the directions card: there is no
      // server-side list for the count to be about.
      title={`Catalog items (${isCreating ? size(newCatalogItems) : catalogItemCount})`}
      // Dropped while creating: every row is a staged addition, so there is nothing to filter to.
      headerAction={
        isCreating ? undefined : (
          <TextField
            select
            size='small'
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            label='Status'
            sx={{ minWidth: 132 }}
          >
            {statusFilterOptions.map(({ value, label }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        )
      }
      // Picked first, added second, in the same shape as the other card's footer. The two steps are
      // separate because choosing the wrong variant of the right drug is the easy mistake here, and
      // an Add button gives a moment to notice the strength before it is staged.
      footer={
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <Autocomplete
            fullWidth
            size='small'
            options={options}
            loading={searching}
            value={selectedCatalogItem}
            filterOptions={(offered) => offered}
            getOptionLabel={({ drugName, strength }) => `${drugName} ${strength ?? ''}`.trim()}
            getOptionDisabled={({ catalogId }) => !isNil(reasonItIsUnpickable(catalogId))}
            isOptionEqualToValue={(option, value) => option.catalogId === value.catalogId}
            onInputChange={handleSearchChange}
            onChange={(_, catalogItem) => setSelectedCatalogItem(catalogItem)}
            noOptionsText={
              isEmpty(options)
                ? 'Type at least 3 characters of a drug name'
                : 'No catalog items found'
            }
            renderOption={(props, { catalogId, drugName, ...packaging }) => (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <li {...props} key={catalogId}>
                <Box>
                  <Typography variant='body2'>{drugName}</Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {describeCatalogItem(packaging)}
                  </Typography>
                  {reasonItIsUnpickable(catalogId) && (
                    <Typography variant='caption' color='text.secondary' display='block'>
                      {reasonItIsUnpickable(catalogId)}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...params}
                label='Search or select a catalog item'
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon fontSize='small' />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <>
                      {searching && <CircularProgress size={16} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Button
            variant='contained'
            disableElevation
            startIcon={<AddIcon />}
            disabled={!selectedCatalogItem}
            onClick={handleAddCatalogItem}
            sx={{ flexShrink: 0 }}
          >
            Add
          </Button>
        </Box>
      }
    >
      {loading && <Skeleton variant='rounded' height={120} />}

      {nothingToShow && (
        <Typography variant='body2' color='text.secondary'>
          {/* eslint-disable-next-line no-nested-ternary */}
          {isCreating
            ? 'Optional — add the catalog items this sig is approved for'
            : size(catalogItems) === 0
              ? 'This sig is not linked to any catalog item yet'
              : 'No catalog items match this filter'}
        </Typography>
      )}

      {!loading && (
        <List dense disablePadding>
          {newCatalogItems.map(({ catalogId, drugName, ...packaging }, index) => (
            <React.Fragment key={catalogId}>
              {index > 0 && <Divider component='li' />}
              <ListItem disableGutters sx={ROW_SX}>
                <ListItemText
                  sx={TEXT_SX}
                  primary={drugName}
                  secondary={describeCatalogItem(packaging)}
                />
                <Box sx={ACTION_GROUP_SX}>
                  <Chip size='small' color='info' variant='outlined' label='Adding' />
                  <Tooltip title='Remove this addition'>
                    <IconButton
                      aria-label={`Remove added catalog item ${drugName}`}
                      onClick={() => onUnstageNew(catalogId)}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </ListItem>
            </React.Fragment>
          ))}

          {visibleCatalogItems.map(
            ({ catalogId, drugName, isActive, isStaged, serverIsActive, ...packaging }, index) => (
              <React.Fragment key={catalogId}>
                {(index > 0 || size(newCatalogItems) > 0) && <Divider component='li' />}
                <ListItem disableGutters sx={ROW_SX}>
                  <ListItemText
                    sx={TEXT_SX}
                    primary={drugName}
                    secondary={describeCatalogItem(packaging)}
                  />
                  <Box sx={ACTION_GROUP_SX}>
                    <AssociationStatusChip
                      isActive={isActive}
                      serverIsActive={serverIsActive}
                      isStaged={isStaged}
                    />
                    <Tooltip title={isActive ? 'Set inactive' : 'Set active'}>
                      <IconButton
                        aria-label={`${isActive ? 'Set inactive' : 'Set active'}: ${drugName}`}
                        onClick={() => onStageStatus({ catalogId, isActive: !isActive })}
                      >
                        {isActive ? <DoDisturbOnOutlinedIcon /> : <CheckCircleOutlineIcon />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </ListItem>
              </React.Fragment>
            )
          )}
        </List>
      )}
    </AssociationCard>
  );
};

export default SigCatalogAssociationsPanel;

const catalogItemShape = {
  catalogId: PropTypes.string,
  drugName: PropTypes.string,
  strength: PropTypes.string,
  form: PropTypes.string,
  packSizeValue: PropTypes.string,
  packSizeUnit: PropTypes.string,
};

SigCatalogAssociationsPanel.propTypes = {
  catalogItems: PropTypes.arrayOf(
    PropTypes.shape({
      ...catalogItemShape,
      isActive: PropTypes.bool,
      isStaged: PropTypes.bool,
      serverIsActive: PropTypes.bool,
    })
  ).isRequired,
  catalogItemCount: PropTypes.number,
  newCatalogItems: PropTypes.arrayOf(PropTypes.shape(catalogItemShape)),
  loading: PropTypes.bool,
  isCreating: PropTypes.bool,
  onStageStatus: PropTypes.func.isRequired,
  onStageNew: PropTypes.func.isRequired,
  onUnstageNew: PropTypes.func.isRequired,
};

SigCatalogAssociationsPanel.defaultProps = {
  catalogItemCount: 0,
  newCatalogItems: [],
  loading: false,
  isCreating: false,
};
